import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import WizardService from '../../../services/wizardService';

const defaultStages = [
  { key: 'eligibility', title: 'Eligibility Check' },
  { key: 'documents', title: 'Documents Collection' },
  { key: 'submission', title: 'Application Submission' },
  { key: 'biometrics', title: 'Biometrics' },
  { key: 'decision', title: 'Decision' }
];

const CaseBuilderPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const submissionId = params.get('submission');
  const clientIdParam = params.get('clientId');
  const [submission, setSubmission] = useState(null);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [pathways, setPathways] = useState([]);
  const [pathwayId, setPathwayId] = useState('');
  const [creating, setCreating] = useState(false);
  const [clientId, setClientId] = useState(clientIdParam || null);
  const [syncingClient, setSyncingClient] = useState(false);
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);
  const [autoAssignInfo, setAutoAssignInfo] = useState(null);

  const formatPathwayName = (key) => WizardService.formatPathwayName(key || '');

  useEffect(() => {
    const loadForSubmission = async () => {
      const { data: sub } = await supabase.from('wizard_submissions').select('*').eq('id', submissionId).single();
      setSubmission(sub || null);

      const { data: p } = await supabase
        .from('immigration_pathways')
        .select(`
          id,
          pathway_key,
          countries (
            name,
            flag_emoji
          )
        `)
        .order('pathway_key');
      setPathways(p || []);
      setPathwayId(sub?.selected_pathway_id || '');
      setTitle(`Case for ${sub?.personal_info?.fullName || sub?.id}`);

      if (sub?.id) {
        try {
          setSyncingClient(true);
          const { data, error } = await supabase.rpc('admin_sync_client_from_submission', {
            p_submission_id: sub.id
          });
          if (error) throw error;
          const resolvedClientId =
            data?.client_id ||
            data?.client?.id ||
            sub.client_id ||
            null;
          setClientId(resolvedClientId);
          if (resolvedClientId && !sub.client_id) {
            setSubmission(prev => ({ ...prev, client_id: resolvedClientId }));
          }
        } catch (err) {
          console.warn('[CaseBuilder] Failed to sync client', err);
        } finally {
          setSyncingClient(false);
        }
      }
    };

    const loadForClient = async () => {
      const { data: p } = await supabase
        .from('immigration_pathways')
        .select(`
          id,
          pathway_key,
          countries (
            name,
            flag_emoji
          )
        `)
        .order('pathway_key');
      setPathways(p || []);
      // Use a generic title when we only know the client, not the submission
      setTitle('New Case');
      setSubmission(null);
      setClientId(clientIdParam || null);
    };

    if (submissionId) {
      loadForSubmission();
    } else {
      loadForClient();
    }
  }, [submissionId, clientIdParam]);

  const autoAssign = async () => {
    try {
      setAutoAssignLoading(true);
      setAutoAssignInfo(null);
      const { data, error } = await supabase.rpc('admin_auto_assign_staff', {
        p_role: submission?.assigned_role || null,
        p_team: submission?.assigned_team || null
      });
      if (error) throw error;
      const staff = data?.staff;
      if (!staff?.email) {
        throw new Error('No staff available for auto-assignment');
      }
      setAssignedTo(staff.email);
      setAutoAssignInfo(`Assigned to ${staff.name || staff.email} (${staff.open_cases || 0} open cases)`);
      if (staff.id) {
        await supabase.rpc('admin_log_staff_event', {
          p_staff_id: staff.id,
          p_event: 'auto_assignment_suggested',
          p_metadata: {
            submission_id: submissionId,
            source: 'case_builder'
          }
        }).catch(() => { });
      }
    } catch (err) {
      console.warn('auto assign error', err);
      alert(err.message || 'Failed to auto assign staff');
    } finally {
      setAutoAssignLoading(false);
    }
  };

  const createCase = async () => {
    try {
      setCreating(true);
      let assignee = assignedTo?.trim();
      let autoAssignedStaffId = null;

      if (!assignee) {
        const { data, error } = await supabase.rpc('admin_auto_assign_staff', {
          p_role: submission?.assigned_role || null,
          p_team: submission?.assigned_team || null
        });
        if (!error) {
          const staff = data?.staff;
          if (staff?.email) {
            assignee = staff.email;
            setAssignedTo(staff.email);
            setAutoAssignInfo(`Assigned to ${staff.name || staff.email} (${staff.open_cases || 0} open cases)`);
            autoAssignedStaffId = staff.id || null;
          }
        }
      }

      const { data: c, error } = await supabase
        .from('cases')
        .insert([{
          submission_id: submissionId,
          client_id: clientId || submission?.client_id || null,
          pathway_id: pathwayId || null,
          title,
          priority,
          assigned_to: assignee || null,
          due_date: dueDate,
          status: 'open',
          current_stage: defaultStages[0].key
        }])
        .select()
        .single();
      if (error) throw error;
      // Seed stages
      const payload = defaultStages.map((s, idx) => ({ case_id: c.id, stage_key: s.key, title: s.title, sort_order: idx }));
      await supabase.from('case_stages').insert(payload);

      if (autoAssignedStaffId) {
        await supabase.rpc('admin_log_staff_event', {
          p_staff_id: autoAssignedStaffId,
          p_event: 'auto_assigned_case',
          p_metadata: {
            case_id: c.id,
            submission_id: submissionId
          }
        }).catch(() => { });
      }

      navigate(`/admin/cases/${c.id}`);
    } catch (e) {
      alert('Failed to create case');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6 space-y-10">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-white mb-4">
          📦 Create Case
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
          Build a comprehensive case from submission data
        </p>
      </div>

      {/* If we are in submission mode and submission hasn't loaded yet, show loading state */}
      {submissionId && !submission && (
        <div className="text-center py-12 text-gray-500">
          Loading submission…
        </div>
      )}

      {(!submissionId || submission) && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 space-y-6">
          {syncingClient && (
            <div className="text-sm text-blue-400 bg-blue-900/20 border border-blue-800 rounded p-4 flex items-center gap-3">
              <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              Syncing client profile from submission…
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-400 mb-3">📝 Case Title</label>
            <input className="w-full border border-gray-600 bg-gray-700 rounded px-4 py-3 text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-400 mb-3">🛜️ Pathway</label>
              <select className="w-full border border-gray-600 bg-gray-700 rounded px-4 py-3 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={pathwayId} onChange={(e) => setPathwayId(e.target.value)}>
                <option value="">Select Pathway</option>
                {pathways.map(p => (
                  <option key={p.id} value={p.id}>
                    {(p.countries?.flag_emoji || '') + ' ' + formatPathwayName(p.pathway_key) + (p.countries?.name ? ` – ${p.countries.name}` : '')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-400 mb-3">🎚️ Priority</label>
              <select className="w-full border border-gray-600 bg-gray-700 rounded px-4 py-3 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {['low', 'medium', 'high'].map(pr => (
                  <option key={pr} value={pr}>
                    {pr === 'low' ? '🟢 Low' : pr === 'medium' ? '🟡 Medium' : '🔴 High'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-400 mb-3">📅 Due Date</label>
              <input type="date" className="w-full border border-gray-600 bg-gray-700 rounded px-4 py-3 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-400 mb-3">👤 Assigned To</label>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <input
                    className="border border-gray-600 bg-gray-700 rounded px-4 py-3 text-sm text-white flex-1 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Staff email or user ID"
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={autoAssign}
                    disabled={autoAssignLoading}
                    className="px-6 py-3 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  >
                    {autoAssignLoading ? '⚙️' : '🎯 Auto'}
                  </button>
                </div>
                {autoAssignInfo && (
                  <div className="text-sm text-emerald-400 bg-emerald-900/20 border border-emerald-800 rounded p-3 font-semibold">
                    ✓ {autoAssignInfo}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-amber-900/20 border-2 border-amber-800 rounded-lg p-6">
            <h4 className="text-sm font-bold text-amber-400 mb-3 flex items-center gap-2">📋 <span>Default Case Stages</span></h4>
            <div className="grid grid-cols-5 gap-3">
              {defaultStages.map((stage, idx) => (
                <div key={stage.key} className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                  <div className="text-xs font-bold text-gray-400">{idx + 1}</div>
                  <div className="text-xs font-semibold text-white mt-1">{stage.title}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4">
            <button disabled={creating} onClick={createCase} className="w-full bg-blue-600 text-white rounded text-xl py-4 font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {creating ? '⚙️ Creating case…' : '✨ Create Case'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseBuilderPage;
